import { RequestConfig } from "./interfaces/api.interface";

export interface APIClientConfig {
  defaultHeaders?: HeadersInit;
  getToken?: () => Promise<string | undefined> | string | undefined;
  baseURL?: string;
}

export class APIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: unknown
  ) {
    super(message);
    this.name = "APIError";
  }
}

export class BaseAPI {
  protected baseURL?: string;
  protected defaultHeaders: HeadersInit;
  protected getToken?: () => Promise<string | undefined> | string | undefined;
  constructor(config: APIClientConfig) {
    this.baseURL = config.baseURL;
    this.defaultHeaders = config.defaultHeaders || {};
    this.getToken = config.getToken;
  }

  protected async getAuthHeaders(): Promise<HeadersInit> {
    const token = await this.getToken?.();
    if (!token) {
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  }

  protected createURL(
    path: string,
    baseURL?: string,
    params?: Record<string, string>
  ): string {
    // baseURL이 필수값이 되어야 합니다
    if (!baseURL) {
      throw new Error("baseURL is required for server-side requests");
    }

    const normalizedBaseURL = baseURL.replace(/\/*$/, "/");
    const normalizedPath = path.replace(/^\/+/, "");

    // URL 생성을 window에 의존하지 않도록 수정
    const finalURL = normalizedBaseURL + normalizedPath;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, value);
      });
      return `${finalURL}?${searchParams.toString()}`;
    }

    return finalURL;
  }

  protected async request<T = any>(
    path: string,
    { params, baseURL = this.baseURL, ...config }: RequestConfig = {}
  ): Promise<T> {
    try {
      const url = this.createURL(path, baseURL, params);
      const authHeaders = await this.getAuthHeaders();

      const headers = new Headers({
        ...this.defaultHeaders,
        ...authHeaders,
        ...config.headers,
      });

      const response = await fetch(url, {
        ...config,
        headers,
        // credentials: "include",
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      throw new APIError(
        error instanceof Error ? error.message : "Network request failed",
        undefined,
        error
      );
    }
  }

  protected async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type");

    if (contentType?.includes("application/json")) {
      const data = await response.json();
      if (!response.ok) {
        // 에러 메시지 처리 더 구체적으로 합니다
        let errorMessage = "API request failed";

        // data.message가 객체인 경우를 처리
        if (data.message) {
          if (typeof data.message === "object") {
            // 객체인 경우 JSON.stringify를 사용하거나
            // 객체의 특정 필드를 사용하도록 처리
            errorMessage = JSON.stringify(data.message);
            // 또는 객체의 특정 필드를 사용할 수 있습니다
            // errorMessage = data.message.detail || data.message.error || JSON.stringify(data.message);
          } else {
            errorMessage = data.message;
          }
        }

        throw new APIError(errorMessage, response.status, data);
      }
      return data;
    }

    if (!response.ok) {
      throw new APIError("API request failed", response.status);
    }

    return response as unknown as T;
  }

  // 편의 메서드들
  async get<T = any>(path: string, config: RequestConfig = {}) {
    return this.request<T>(path, { ...config, method: "GET" });
  }

  async post<T = any>(
    path: string,
    data?: any,
    config: Omit<RequestConfig, "body"> = {}
  ) {
    // FormData인 경우 직접 body로 전달
    if (data instanceof FormData) {
      return this.request<T>(path, {
        ...config,
        method: "POST",
        body: data, // FormData는 직접 전달
      });
    }

    // JSON 데이터인 경우 기존 로직 유지
    const headers = {
      "Content-Type": "application/json",
      ...config.headers,
    };

    return this.request<T>(path, {
      ...config,
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
  }

  async delete<T = any>(path: string, config: RequestConfig = {}) {
    return this.request<T>(path, { ...config, method: "DELETE" });
  }

  async put<T = any>(
    path: string,
    data?: any,
    config: Omit<RequestConfig, "body"> = {}
  ) {
    return this.request<T>(path, {
      ...config,
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  protected async patch<T>(
    path: string,
    data?: any,
    config: Omit<RequestConfig, "body"> = {}
  ) {
    // FormData인 경우 직접 body로 전달
    if (data instanceof FormData) {
      return this.request<T>(path, {
        ...config,
        method: "PATCH",
        body: data, // FormData는 직접 전달
      });
    }

    const headers = {
      "Content-Type": "application/json",
      ...config.headers,
    };

    return this.request<T>(path, {
      ...config,
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    });
  }

  // DELETE 등 다른 메서드들도 유사하게 구현...
}
